import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { onScreenKey, openOnScreen, type OnScreen } from "./onScreen";
import { useOnScreenStore } from "./onScreenStore";

/**
 * Publish the record this page shows while it is mounted (null: nothing).
 * Selection and unsaved state update in place; leaving the page clears it,
 * and the record stays in the recently viewed list.
 */
export const useOnScreen = (entry: OnScreen | null): void => {
  const publish = useOnScreenStore((s) => s.publish);
  const clear = useOnScreenStore((s) => s.clear);
  const signature = entry ? JSON.stringify(entry) : null;
  const key = entry ? onScreenKey(entry) : null;

  useEffect(() => {
    if (signature) publish(JSON.parse(signature) as OnScreen);
  }, [signature, publish]);

  useEffect(() => {
    if (!key) return undefined;
    return () => clear(key);
  }, [key, clear]);
};

/** The record the panel will send with the next turn, for the composer chip. */
export const useOpenOnScreen = (): OnScreen | null => {
  const { pathname, search } = useLocation();
  const current = useOnScreenStore((s) => s.current);
  const dismissedKey = useOnScreenStore((s) => s.dismissedKey);
  return useMemo(
    () => openOnScreen(current, `${pathname}${search}`, dismissedKey),
    [current, pathname, search, dismissedKey]
  );
};
